// ⚠️ This file is over 150 lines. Consider splitting it.
import React, { useRef, useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import useAdminRole from '../../hooks/useAdminRole'
import { useBranding } from '../../context/BrandingContext'
import apiFetch from '@utils/apiFetch'
const Header = ({ setHeaderHeight }) => {
    const headerRef = useRef(null)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [user, setUser] = useState(null)
    const { role } = useAdminRole()
    const branding = useBranding()
    const navigate = useNavigate()
    const location = useLocation()
    useEffect(() => {
        if (headerRef.current) {
            setHeaderHeight(headerRef.current.offsetHeight)
        }
        const checkAuth = () => {
            const token = localStorage.getItem('token')
            setIsAuthenticated(!!token)
        }
        checkAuth()
        window.addEventListener('storage', checkAuth)
        return () => window.removeEventListener('storage', checkAuth)
    }, [setHeaderHeight])
    useEffect(() => {
        const token = localStorage.getItem('token')
        const auth = !!token
        setIsAuthenticated(auth)
        if (auth) {
        apiFetch('/api/auth/me')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load user')
                return res.json()
            })
            .then((data) => setUser(data))
            .catch(() => {
                // ignore profile load errors
                setUser(null)
            })
        } else {
            setUser(null)
        }
    }, [location.pathname])
    const closeMenu = () => setIsMenuOpen(false)
    const isActive = (path) => location.pathname === path
    const navLinkClass = (path) =>
        isActive(path)
            ? 'text-primary font-semibold border-b-2 border-primary pb-0.5'
            : ''
    const handleLogout = () => {
        localStorage.removeItem('token')
        setIsAuthenticated(false)
        navigate('/') // Redirect to HeroPage
    }
    return (
        <>
            <header ref={headerRef} className="fixed top-0 w-full bg-bg-light shadow-md z-50 py-3">
                <div className="container mx-auto flex items-center justify-between px-4">
                    <Link
                        to="/user-dashboard"
                        className="flex items-center space-x-3 hover:opacity-80 transition">
                        {branding?.logo && (
                            <img
                                src={branding.logo}
                                alt={`${branding.brandName} logo`}
                                className="h-12 w-auto object-contain"
                            />
                        )}
                        <h1 className="text-base sm:text-lg md:text-xl font-bold text-text">
                            {branding?.brandName}
                        </h1>
                    </Link>
                    <div className="flex items-center space-x-4">
                        <div className="hidden sm:flex items-center space-x-4">
                            {isAuthenticated && user && (
                                <Link to="/profile" className="text-text hover:text-accent font-medium">
                                    {user.firstName}
                                </Link>
                            )}
                            {!isAuthenticated ? (
                                <Link to="/login" className="auth-button">
                                    Login
                                </Link>
                            ) : (
                                <button onClick={handleLogout} className="auth-button">
                                    Logout
                                </button>
                            )}
                        </div>
                        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="menu-button">
                            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </header>
            {isMenuOpen && <div className="menu-overlay" onClick={closeMenu}></div>}
            <nav
                className={`menu-drawer ${
                    isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                } flex flex-col justify-between`}>
                <ul className="menu-list">
                    {isAuthenticated && (
                        <li>
                            <Link to="/event" onClick={closeMenu} className={navLinkClass('/event')}>
                                Submit Event
                            </Link>
                        </li>
                    )}
                    {isAuthenticated && (
                        <li>
                            <Link to="/user-dashboard" onClick={closeMenu} className={navLinkClass('/user-dashboard')}>
                                User Dashboard
                            </Link>
                        </li>
                    )}
                    {role === 'technical_owner' && (
                        <>
                            <li>
                                <Link to="/dashboard/technical" onClick={closeMenu} className={navLinkClass('/dashboard/technical')}>
                                    Technical Dashboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/display" onClick={closeMenu} className={navLinkClass('/dashboard/display')}>
                                    Display Dashboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/moderator" onClick={closeMenu} className={navLinkClass('/dashboard/moderator')}>
                                    Moderator Dashboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/project-map" onClick={closeMenu} className={navLinkClass('/dashboard/project-map')}>
                                    Project Map
                                </Link>
                            </li>
                        </>
                    )}
                    {role === 'display_admin' && (
                        <li>
                            <Link to="/dashboard/display" onClick={closeMenu} className={navLinkClass('/dashboard/display')}>
                                Display Dashboard
                            </Link>
                        </li>
                    )}
                    {role === 'event_moderator' && (
                        <li>
                            <Link to="/dashboard/moderator" onClick={closeMenu} className={navLinkClass('/dashboard/moderator')}>
                                Moderator Dashboard
                            </Link>
                        </li>
                    )}
                </ul>
                <div className="px-4 pb-4 border-t pt-3">
                    <Link to="/settings" onClick={closeMenu} className="text-text hover:text-accent block">
                        ⚙️ Settings
                    </Link>
                    <Link to="/help" onClick={closeMenu} className="text-text hover:text-accent block">
                        ❓ Help
                    </Link>
                    <Link to="/contact" onClick={closeMenu} className="text-text hover:text-accent block">
                        ✉️ Contact Support
                    </Link>
                    {isAuthenticated && user && (
                        <Link
                            to="/profile"
                            onClick={closeMenu}
                            className="w-full mt-2 font-medium text-text sm:hidden block">
                            {user.firstName}'s Profile
                        </Link>
                    )}
                    {!isAuthenticated ? (
                        <Link to="/login" onClick={closeMenu} className="auth-button w-full mt-2 sm:hidden">
                            Login
                        </Link>
                    ) : (
                        <button
                            onClick={() => {
                                handleLogout()
                                closeMenu()
                            }}
                            className="auth-button w-full mt-2 sm:hidden">
                            Logout
                        </button>
                    )}
                </div>
            </nav>
        </>
    )
}
export default Header
